import classNames from 'classnames';
import { useCallback, useRef, type MouseEvent, type PointerEvent, type ReactNode } from 'react';
import { Icon, usePopover } from '@sorrem/ui';
import styles from './InfoIcon.module.scss';

interface InfoIconProps {
    ariaLabel?: string;
    popoverContent: ReactNode;
    popoverPlacement?: 'top' | 'bottom' | 'left' | 'right';
    /** When true, clicking the icon pins the popover open. */
    pinnable?: boolean;
    className?: string;
}

export function InfoIcon({
    ariaLabel,
    popoverContent,
    popoverPlacement = 'top',
    pinnable = false,
    className,
}: InfoIconProps): JSX.Element {
    const suppressClickRef = useRef(false);
    const { triggerRef, showPopover, hidePopover, pinPopover, isPopoverVisible } = usePopover({
        delay: 120,
        placement: popoverPlacement,
        pinnable,
    });

    const handleShowPopover = useCallback((): void => {
        showPopover(popoverContent);
    }, [popoverContent, showPopover]);

    const handlePointerDown = useCallback((event: PointerEvent<HTMLSpanElement>): void => {
        if (pinnable && isPopoverVisible) {
            event.preventDefault();
            suppressClickRef.current = true;
            pinPopover();
        }
    }, [pinnable, isPopoverVisible, pinPopover]);

    const handleClick = useCallback((event: MouseEvent<HTMLSpanElement>): void => {
        if (suppressClickRef.current) {
            event.preventDefault();
            suppressClickRef.current = false;
        }
    }, []);

    return (
        <div ref={triggerRef} className={classNames(styles.anchor, className)}>
            <span
                className={styles.infoIcon}
                aria-label={ariaLabel}
                aria-haspopup="dialog"
                tabIndex={0}
                onMouseEnter={handleShowPopover}
                onMouseLeave={hidePopover}
                onFocus={handleShowPopover}
                onBlur={hidePopover}
                onPointerDown={handlePointerDown}
                onClick={handleClick}
            >
                <Icon icon="info" className={styles.icon} strokeWidth={2} />
            </span>
        </div>
    );
}
